<script setup lang="ts">
import type { PeriodI, UniqueLabel } from "@lxc/app-device-common";
import { filterEmptyValues, IntlDateTools } from "@lxc/app-device-common";
import dayjs from "dayjs";
import { v4 as uuidv4 } from "uuid";
import type { Ref } from "vue";
import { watch } from "vue";
import { useAcl } from "vue-simple-acl";
import { FILTER_DATE_FORMAT } from "~/core/constants/constants";
import { ACLRoles } from "~/core/models/ACLRoles.enum";
import type {
  FilterFormSection,
  FilterSelectionDefinition,
  FilterSelectionValue,
  FiltersSelection,
  Option,
} from "~/core/models/filters";
import { FilterInputType, Filters } from "~/core/models/filters";
import { getPeriod } from "~/core/utils/date";
import type { Dtwin } from "~/modules/dtwin/models/Dtwin.interface";
import { useLogActionActionTypesFilters } from "~/modules/log/composables/useLogActionActionTypesFilters";
import { useEntityFilters } from "~/modules/log/composables/useLogEntityFilters";
import { useLogFilterTags } from "~/modules/log/composables/useLogFilterTags";
import { ActionsOrActionTypesContext } from "~/modules/log/models/ActionsOrActionTypesContext.enum";
import type { AsyncLog } from "~/modules/log/models/AsyncLog.interface";
import type { LogComponentId } from "~/modules/log/models/LogComponentId.enum";
import { LogEntityClass } from "~/modules/log/models/LogEntityClass.enum";
import { LogEventType } from "~/modules/log/models/LogEventType.enum";
import { LogLevel } from "~/modules/log/models/LogLevel.enum";
import type { LogSortBy } from "~/modules/log/models/LogSortBy.enum";
import type { LogSortDirection } from "~/modules/log/models/LogSortDirection.enum";
import { Origin } from "~/modules/log/models/Origin.enum";
import type { TranslatedLogAttribute } from "~/modules/log/models/TranslatedLogAttribute.interface";
import type { Role } from "~/modules/user/models/Role.interface";
import type { User } from "~/modules/user/models/User.interface";
import type { UserGroup } from "~/modules/user/models/UserGroup.interface";
import type { UserSessionSector } from "~/modules/user/models/UserSessionSector.interface";

const props = defineProps<{
  filters: FiltersSelection;
  rowsSelected: AsyncLog[];
  businessUnitCode?: number;
  organizationCode?: number;
  logs?: AsyncLog[];
}>();
const emit = defineEmits(["change", "enter"]);

const { locale, t } = useI18n();

const { can } = useAcl();

const {
  actions,
  actionTypes,
  updateAppliedFilters: updateActionsActionTypesAppliedFilters,
  getFilterValues: getActionsActionTypesFilterValues,
  clearFilters: clearActionsActionTypesFilters,
  initializeFromFilters: initializeActionsActionTypesFromFilters,
  appliedActions,
  appliedActionTypes,
} = useLogActionActionTypesFilters({
  businessUnitCode: props.businessUnitCode,
  organizationCode: props.organizationCode,
});

const {
  entityClass,
  devices,
  users,
  entityId,
  loadEntitiesByClass,
  getFilterValues: getEntityFilterValues,
  clearEntities,
  appliedDevices,
  appliedUsers,
  appliedUserGroups,
  appliedRoles,
  appliedSectors,
  isEntityLoading,
} = useEntityFilters({
  organizationCode: props.organizationCode,
});

const updateInProgress = ref(false);
const applyPending = ref(false);
let onPropsEntityChangedPending = false;

const componentId: Ref<LogComponentId | undefined> = ref(
  props.filters.get(Filters.LOG_COMPONENT_ID)?.value as
    | LogComponentId
    | undefined,
);
const sortBy: Ref<LogSortBy | undefined> = ref(
  props.filters.get(Filters.SORT_BY)?.value as LogSortBy | undefined,
);
const sortDirection: Ref<LogSortDirection | undefined> = ref(
  props.filters.get(Filters.SORT_DIRECTION)?.value as
    | LogSortDirection
    | undefined,
);

const tagsetId = `tagset-${uuidv4()}`;
const tagsetSelector = `#${tagsetId}`;

const deviceLabel = t("logs.filters.entityClass.value.device");
const sectorLabel = t("logs.filters.entityClass.value.sector");
const profileLabel = t("logs.filters.entityClass.value.profile");
const roleLabel = t("logs.filters.entityClass.value.role");
const userGroupLabel = t("logs.filters.entityClass.value.group");
const userLabel = t("logs.filters.entityClass.value.user");
const actionLabel = t("logs.action.label");
const actionTypeLabel = t("logs.actionType.label");
const periodSeparator = ref(` ${t("logs.filters.timestamp.periodSeparator")} `);
const eventTypeLabel = t("logs.eventType.label");
const levelLabel = t("logs.level.label");
const originLabel = t("logs.origin.label");

const { selectedTags, buildAllTags, deleteTag } = useLogFilterTags({
  actions: actions,
  actionTypes: actionTypes,
  entityClass: entityClass,
  devices: computed(() => devices.value),
  users: computed(() => users.value),
  actionLabel,
  actionTypeLabel,
  deviceLabel,
  userLabel,
  userGroupLabel,
  profileLabel,
  roleLabel,
  sectorLabel,
});

const selectedFiltersByTypes = computed<
  (
    | string
    | LogComponentId
    | LogEntityClass
    | (string | LogLevel | undefined | null)[]
    | undefined
    | null
  )[]
>(() => [
  componentId.value,
  entityId.value,
  actions.value?.map((translated) => translated.rawValue),
  actionTypes.value?.map((translated) => translated.rawValue),
]);

const formPeriod = computed({
  get(): PeriodI {
    const appliedStartStringDate = props.filters.get(Filters.LOG_START_DATE)
      ?.value as string | undefined;
    const appliedEndStringDate = props.filters.get(Filters.LOG_END_DATE)
      ?.value as string | undefined;

    if (appliedStartStringDate && appliedEndStringDate) {
      return {
        startDate: IntlDateTools.parse(
          appliedStartStringDate,
          FILTER_DATE_FORMAT,
          locale.value,
        ),
        endDate: IntlDateTools.parse(
          appliedEndStringDate,
          FILTER_DATE_FORMAT,
          locale.value,
        ),
      };
    } else {
      return getPeriod();
    }
  },
  set(newValue: PeriodI) {
    const { startDate, endDate } = getPeriod(
      newValue?.startDate,
      newValue?.endDate,
    );

    emit("change", Filters.LOG_START_DATE, dayjs(startDate).format());
    emit("change", Filters.LOG_END_DATE, dayjs(endDate).format());
    emit("enter");
  },
});

const canClearPeriodSelection = computed(() => {
  const todayPeriod = getPeriod();
  const selectedPeriod = getPeriod(
    formPeriod.value.startDate,
    formPeriod.value.endDate,
  );
  return (
    todayPeriod.startDate?.getTime() !== selectedPeriod.startDate?.getTime() ||
    todayPeriod.endDate?.getTime() !== selectedPeriod.endDate?.getTime()
  );
});

const onEntityLoadingUpdated = (loading: boolean) => {
  if (!loading && onPropsEntityChangedPending) {
    onPropsEntityChangedPending = false;
    loadEntitiesByClass(
      props.filters.get(Filters.LOG_AUTHOR_CLASS)?.value as
        | LogEntityClass
        | undefined,
      props.filters.get(Filters.LOG_AUTHOR_ID)?.value as string[] | undefined,
    );
  }
};

const onChange = (filter: Filters, value: FilterSelectionValue) => {
  emit("change", filter, value);
};

const onEnter = (event: Event) => {
  emit("enter", event);
};

const logLevelOptions: Ref<(Option | Record<string, any>)[]> = ref(
  Object.values(LogLevel).map((value) => {
    return {
      value,
      label: value ? `logs.level.value.${value}` : "",
    };
  }),
);

const eventTypeOptions = computed<Option[]>((): Option[] => {
  return Object.values(LogEventType).map((value: LogEventType): Option => {
    const label = value ? t(`logs.eventType.value.${value}`) : "";
    return {
      value,
      label,
    };
  });
});

const originOptions: Ref<Option[]> = ref([
  {
    label: "logs.origin.values.device",
    value: Origin.DEVICE,
  },
  {
    label: "logs.origin.values.mobileApplication",
    value: Origin.MOBILE_APPLICATION,
  },
  {
    label: "logs.origin.values.thirdPartyApplication",
    value: Origin.THIRD_PARTY_APPLICATION,
  },
  {
    label: "logs.origin.values.lxConnect",
    value: Origin.LX_CONNECT,
  },
]);

const filterFormSections: FilterFormSection[] = [
  {
    disabled: false,
    filter: Filters.LOG_LEVEL,
    footerEnabled: true,
    footerId: "level-footer",
    header: levelLabel,
    id: "level",
    inputType: FilterInputType.CHECKBOX,
    menuLabel: levelLabel,
    options: logLevelOptions,
    tagPrefix: levelLabel,
    translate: true,
  },
  {
    disabled: false,
    filter: Filters.LOG_ORIGIN,
    footerEnabled: true,
    footerId: "origin-footer",
    header: originLabel,
    id: "origin",
    inputType: FilterInputType.CHECKBOX,
    menuLabel: originLabel,
    options: originOptions,
    tagPrefix: originLabel,
    translate: true,
  },
  {
    disabled: false,
    filter: Filters.LOG_EVENT_TYPE,
    footerId: "eventType-footer",
    footerEnabled: true,
    header: eventTypeLabel,
    inputType: FilterInputType.CHECKBOX,
    id: "eventType",
    menuLabel: eventTypeLabel,
    options: eventTypeOptions,
    tagPrefix: eventTypeLabel,
    translate: false,
  },
  {
    disabled: false,
    filter: Filters.LOG_ACTION,
    footerEnabled: true,
    footerId: "action-footer",
    header: actionLabel,
    id: "action",
    inputType: FilterInputType.CUSTOM,
    menuLabel: actionLabel,
    tagPrefix: actionLabel,
    translate: false,
  },
  {
    disabled: false,
    filter: Filters.LOG_ACTION_TYPE,
    footerEnabled: true,
    footerId: "action-type-footer",
    header: actionTypeLabel,
    id: "action-type",
    inputType: FilterInputType.CUSTOM,
    menuLabel: actionTypeLabel,
    tagPrefix: actionTypeLabel,
    translate: false,
  },
  {
    additionalFilter: Filters.LOG_AUTHOR_CLASS,
    disabled: false,
    filter: Filters.LOG_AUTHOR_ID,
    footerEnabled: true,
    footerId: "user-footer",
    header: userLabel,
    id: LogEntityClass.USER,
    inputType: FilterInputType.CUSTOM,
    menuLabel: userLabel,
    tagPrefix: userLabel,
  },
];

if (can(ACLRoles.CAN_READ_DEVICE) && props.organizationCode) {
  filterFormSections.push({
    additionalFilter: Filters.LOG_AUTHOR_CLASS,
    disabled: false,
    filter: Filters.LOG_AUTHOR_ID,
    footerEnabled: true,
    footerId: "device-footer",
    header: deviceLabel,
    id: LogEntityClass.DEVICE,
    inputType: FilterInputType.CUSTOM,
    menuLabel: deviceLabel,
    tagPrefix: deviceLabel,
  });
}

function onEntityClassChanged(newEntityClass?: LogEntityClass) {
  // Disable a form section if another form section has filters
  for (const entityClass of [LogEntityClass.USER, LogEntityClass.DEVICE]) {
    let filterFormSection: FilterFormSection | undefined;

    filterFormSection = filterFormSections.find(
      (formSection) => formSection.id === entityClass,
    );

    if (filterFormSection) {
      filterFormSection.disabled =
        !!newEntityClass && entityClass !== newEntityClass;
    }
  }
}

function onAppliedFilterChange() {
  updateActionsActionTypesAppliedFilters();

  const appliedEntityClass = props.filters.get(Filters.LOG_AUTHOR_CLASS)
    ?.value as string | undefined;

  const appliedEntityId = props.filters.get(Filters.LOG_AUTHOR_ID)?.value as
    | string[]
    | undefined;

  loadEntitiesByClass(appliedEntityClass, appliedEntityId);
}

function applyFilter() {
  if (!updateInProgress.value) {
    const { actions, actionTypes } = getActionsActionTypesFilterValues();
    emit("change", Filters.LOG_ACTION, actions);
    emit("change", Filters.LOG_ACTION_TYPE, actionTypes);

    const { entityClass, entityId } = getEntityFilterValues();
    emit("change", Filters.LOG_AUTHOR_CLASS, entityClass);
    emit("change", Filters.LOG_AUTHOR_ID, entityId);

    emit("enter");
  } else {
    applyPending.value = true;
  }
}

const onUpdateInProgressChanged = (inProgress: boolean) => {
  if (!inProgress && applyPending.value) {
    applyFilter();
    applyPending.value = false;
  }
};

function clearFilter() {
  updateInProgress.value = true;
  componentId.value = undefined;

  clearEntities();
  clearActionsActionTypesFilters();

  sortBy.value = undefined;
  sortDirection.value = undefined;

  updateInProgress.value = false;
  applyFilter();
}

function discardFilter() {
  updateInProgress.value = true;

  componentId.value = props.filters.get(Filters.LOG_COMPONENT_ID)?.value as
    | LogComponentId
    | undefined;
  const entityClass = props.filters.get(Filters.LOG_AUTHOR_CLASS)?.value as
    | LogEntityClass
    | undefined;
  sortBy.value = props.filters.get(Filters.SORT_BY)?.value as
    | LogSortBy
    | undefined;
  sortDirection.value = props.filters.get(Filters.SORT_DIRECTION)?.value as
    | LogSortDirection
    | undefined;

  switch (entityClass) {
    case LogEntityClass.DEVICE:
      devices.value = appliedDevices.value?.slice(0) ?? [];
      break;
    case LogEntityClass.USER:
      users.value = appliedUsers.value?.slice(0) ?? [];
      break;
    default:
      break;
  }

  updateInProgress.value = false;
}

function updateEntityFilterSection(
  paramFilterMap: FiltersSelection,
  entityClass: LogEntityClass,
  paramDevices?: Dtwin[],
  paramUsers?: User[],
  paramUserGroups?: UserGroup[],
  paramRoles?: Role[],
  paramSectors?: UserSessionSector[],
) {
  let entityIds: string[] = [];

  switch (entityClass) {
    case LogEntityClass.DEVICE:
      if (entityClass === LogEntityClass.DEVICE) {
        if (paramDevices) {
          entityIds = filterEmptyValues<string>(
            paramDevices.map((device) => device.uid),
          );
        }
      }
      break;
    case LogEntityClass.GROUP:
      if (paramUserGroups) {
        entityIds = filterEmptyValues<string>(
          paramUserGroups.map((userGroup) => userGroup.code),
        );
      }
      break;
    case LogEntityClass.ROLE:
      if (paramRoles) {
        entityIds = filterEmptyValues<string>(
          paramRoles.map((role) => (role.id ? String(role.id) : undefined)),
        );
      }
      break;
    case LogEntityClass.SECTOR:
      if (paramSectors) {
        entityIds = filterEmptyValues<string>(
          paramSectors.map((sector) => sector.code),
        );
      }
      break;
    case LogEntityClass.USER:
      if (paramUsers) {
        entityIds = filterEmptyValues<string>(
          paramUsers.map((user) => user.login),
        );
      }
      break;
    default:
      break;
  }

  paramFilterMap.set(Filters.LOG_AUTHOR_CLASS, {
    key: "authorClass",
    operator: "=",
    value: entityClass,
  });

  paramFilterMap.set(Filters.LOG_AUTHOR_ID, {
    key: "authorId",
    operator: "=",
    value: entityIds,
  });
}

function updateFilterSelection(
  filters: FiltersSelection,
  actions?: TranslatedLogAttribute[],
  actionTypes?: TranslatedLogAttribute[],
  entityClass?: LogEntityClass,
  paramDevices?: Dtwin[],
  paramUsers?: User[],
  paramGroups?: UserGroup[],
  paramRoles?: Role[],
  paramSectors?: UserSessionSector[],
): FiltersSelection {
  filters.set(Filters.LOG_ACTION, {
    key: "action",
    operator: "=",
    value:
      actions?.map(
        (translatedLogAttribute) => translatedLogAttribute.rawValue,
      ) ?? [],
  });
  filters.set(Filters.LOG_ACTION_TYPE, {
    key: "actionType",
    operator: "=",
    value:
      actionTypes?.map(
        (translatedLogAttribute) => translatedLogAttribute.rawValue,
      ) ?? [],
  });

  if (entityClass) {
    updateEntityFilterSection(
      filters,
      entityClass,
      paramDevices,
      paramUsers,
      paramGroups,
      paramRoles,
      paramSectors,
    );
  }

  return filters;
}

const selectedFilterMap = computed((): FiltersSelection => {
  const filterSelection: FiltersSelection = new Map<
    Filters,
    FilterSelectionDefinition
  >();
  return updateFilterSelection(
    filterSelection,
    actions.value,
    actionTypes.value,
    entityClass.value,
    devices.value,
    users.value,
  );
});

const appliedFilterTags = computed((): UniqueLabel[] => {
  const appliedEntityClass = props.filters.get(Filters.LOG_AUTHOR_CLASS)
    ?.value as LogEntityClass | undefined;

  return buildAllTags(
    appliedActions.value,
    appliedActionTypes.value,
    appliedEntityClass,
    appliedDevices.value,
    appliedUsers.value,
    appliedUserGroups.value,
    appliedRoles.value,
    appliedSectors.value,
  );
});

function onDeleteSelectedTag(tag: string, uid?: string) {
  updateInProgress.value = true;
  deleteTag(
    uid ?? tag,
    actions,
    actionTypes,
    entityId,
    entityClass,
    computed(() => devices.value),
    computed(() => users.value),
  );
  updateInProgress.value = false;
}

function onAppliedTagDeleteClick(tag: string, uid?: string) {
  updateInProgress.value = true;

  deleteTag(
    uid ?? tag,
    actions,
    actionTypes,
    entityId,
    entityClass,
    computed(() => devices.value),
    computed(() => users.value),
  );

  updateInProgress.value = false;
  applyFilter();
}

watch(() => entityClass.value, onEntityClassChanged);
watch(() => isEntityLoading.value, onEntityLoadingUpdated);
watch(() => updateInProgress.value, onUpdateInProgressChanged);

defineExpose({
  clearFilter,
});

onMounted(async () => {
  await initializeActionsActionTypesFromFilters(props.filters);

  onAppliedFilterChange();
});
</script>

<template>
  <div class="relative flex justify-between">
    <log-download :data="props.rowsSelected" />
    <div class="relative flex justify-end">
      <lxc-period-picker
        v-model="formPeriod"
        type="primary"
        :can-clear-selection="canClearPeriodSelection"
        button-size
        :formatter="t('logs.filters.timestamp.formatter')"
        :i18n="locale"
        :separator="periodSeparator"
        :placeholder="t('logs.filters.timestamp.placeholder')"
        class="mr-3"
      />

      <filters-component
        v-if="actions && actionTypes"
        :applied-tags="appliedFilterTags"
        :filter-sections="filterFormSections"
        :filters="props.filters"
        :filters-by-type="selectedFiltersByTypes"
        :selected-filters="selectedFilterMap"
        :selected-tags="selectedTags"
        :teleported-tags="tagsetSelector"
        @apply="applyFilter"
        @applied-filter-change="onAppliedFilterChange"
        @change="onChange"
        @delete="onAppliedTagDeleteClick"
        @delete-selected="onDeleteSelectedTag"
        @enter="onEnter"
        @discard="discardFilter"
        @reset="clearFilter"
      >
        <template #action>
          <log-action-action-type-filter
            v-model="actions"
            :context="ActionsOrActionTypesContext.ACTION"
            :label="actionLabel"
          />
        </template>

        <template #action-type>
          <log-action-action-type-filter
            v-model="actionTypes"
            :context="ActionsOrActionTypesContext.ACTION_TYPES"
            :label="actionTypeLabel"
          />
        </template>

        <template #[LogEntityClass.USER]>
          <log-user-filter
            v-model="users.value"
            :label="userLabel"
            :business-unit-code="businessUnitCode"
            :organization-code="organizationCode"
          />
        </template>

        <template #[LogEntityClass.DEVICE]>
          <log-device-filter
            v-model="devices.value"
            :label="deviceLabel"
            :organization-code="organizationCode"
          />
        </template>
      </filters-component>
    </div>
  </div>
  <div :id="tagsetId" />
</template>
